import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import AdminTable from "./components/AdminTable";
import AddAdminForm from "./components/AddAdminForm";
import AuditTrail from "./components/AuditTrail";
import ConfirmationModal from "./components/ConfirmationModal";
import { useDispatch, useSelector } from "react-redux";
import { getUsersService } from "redux/services/users_service";
import { getPermissionsService } from "redux/services/general_service";
import Pagination from "./components/Pagination";
import { useLanguage } from "LanguageContext";

const AdminAccountManagement = () => {
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const { language } = useLanguage();

  const permissions = JSON.parse(localStorage.getItem("permissions"));

  const createUserExists = permissions.some(
    (permission) => permission.name === "create_user"
  );
  const viewUserExists = permissions.some(
    (permission) => permission.name === "view_user"
  );

  const dispatch = useDispatch();

  const { usersList, metaUsers } = useSelector((state) => state.users);

  const [currentPage, setCurrentPage] = useState(1);

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
  };

  const handleDeactivateAdmin = (admin) => {
    setSelectedAdmin(admin);
    setConfirmAction("deactivate");
    setShowConfirmModal(true);
  };

  const handleDeleteAdmin = (admin) => {
    setSelectedAdmin(admin);
    setConfirmAction("delete");
    setShowConfirmModal(true);
  };

  const confirmActionHandler = () => {
    if (confirmAction === "deactivate") {
      setAdmins((prev) =>
        prev.map((admin) =>
          admin.id === selectedAdmin.id
            ? {
                ...admin,
                status: admin.status === "active" ? "inactive" : "active",
              }
            : admin
        )
      );
    } else if (confirmAction === "delete") {
      setAdmins((prev) =>
        prev.filter((admin) => admin.id !== selectedAdmin.id)
      );
    }
    setShowConfirmModal(false);
    setSelectedAdmin(null);
    setConfirmAction(null);
  };

  const handleAddAdmin = (newAdmin) => {
    const admin = {
      ...newAdmin,
      id: Date.now(),
      lastLogin: "Never",
      status: "active",
      createdDate: new Date().toISOString().split("T")[0],
    };
    setAdmins((prev) => [...prev, admin]);
  };

  const handleSearch = (val) => {
    dispatch(getUsersService({ search: val }));
  };

  const translations = {
    en: {
      title: "Admin Account Management",
      subtitle: "Manage administrator accounts and permissions",
      existingAdmins: "Existing Admin Accounts",
      addNewAdmin: "Add New Admin",
      auditTrail: "Recent Admin Activities",
      searchPlaceholder: "Search admins...",
      language: "Language",
    },
    ar: {
      title: "إدارة حسابات المشرفين",
      subtitle: "إدارة حسابات المشرفين والصلاحيات",
      existingAdmins: "حسابات المشرفين الحالية",
      addNewAdmin: "إضافة مشرف جديد",
      auditTrail: "الأنشطة الأخيرة للمشرفين",
      searchPlaceholder: "البحث عن المشرفين...",
      language: "اللغة",
    },
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(getUsersService({ page: page }));
    // Add any additional logic you want to execute on page change
  };

  const t = translations[language];

  useEffect(() => {
    dispatch(getUsersService({ page: 1 }));
    dispatch(getPermissionsService());
  }, []);

  return (
    <div
      className={`min-h-screen bg-gray-50 ${language === "ar" ? "rtl" : "ltr"}`}
    >
      <div className="flex">
        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8 mt-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t.title}
              </h1>
              <p className="text-gray-600">{t.subtitle}</p>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Admin List Panel */}
              <div className="lg:col-span-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <h2 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-0">
                        {t.existingAdmins}
                      </h2>
                      <div className="relative">
                        <Icon
                          name="Search"
                          size={20}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                          type="text"
                          placeholder={t.searchPlaceholder}
                          onChange={(e) => handleSearch(e.target.value)}
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {viewUserExists && (
                    <AdminTable
                      admins={usersList}
                      onEdit={handleEditAdmin}
                      onDeactivate={handleDeactivateAdmin}
                      onDelete={handleDeleteAdmin}
                      language={language}
                    />
                  )}

                  {/* Pagination */}
                  {metaUsers?.total > 9 && (
                    <Pagination
                      currentPage={metaUsers?.current_page}
                      totalPages={metaUsers?.last_page}
                      onPageChange={handlePageChange}
                    />
                  )}
                </div>
              </div>

              {/* Add Admin Form Panel */}
              <div className="lg:col-span-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t.addNewAdmin}
                    </h2>
                  </div>

                  {createUserExists && (
                    <AddAdminForm
                      onAddAdmin={handleAddAdmin}
                      selectedAdmin={selectedAdmin}
                      onClearSelection={() => setSelectedAdmin(null)}
                      language={language}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Audit Trail Section 
            <div className="mt-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t.auditTrail}
                  </h2>
                </div>

                <AuditTrail language={language} />
              </div>
            </div>*/}
          </div>
        </main>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmActionHandler}
          action={confirmAction}
          adminName={selectedAdmin?.name}
          language={language}
        />
      )}
    </div>
  );
};

export default AdminAccountManagement;
